import { post } from '@/services/apiClient';

const API_BASE = '/api/v1/datasheets';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DetectedPage {
  page: number;
  bbox: { x1: number; y1: number; x2: number; y2: number } | null;
  page_png_base64: string;
}

export interface DetectFootprintResponse {
  pages: DetectedPage[];
  source_filename: string;
  detection_description?: string;
}

export interface ExtractCroppedResponse {
  extracted_dimensions: Record<string, unknown>;
  footprint: Record<string, unknown> | null;
  cropped_png_base64: string;
  generation_error?: string;
}

/** Component info nested under frontmatter.component */
export interface DatasheetComponent {
  manufacturer?: string;
  part_number?: string;
  package?: string;
  category?: string;
  datasheet_source?: string;
}

export interface DatasheetFrontmatter {
  schema_version?: string;
  component?: DatasheetComponent;
  // Also support flat fields for backwards compatibility
  manufacturer?: string;
  part_number?: string;
  description?: string;
  package?: string;
  category?: string;
  status?: string;
  pins?: Array<{ number: number; name: string; type: string }>;
  absolute_max_ratings?: Record<string, unknown>;
  electrical_characteristics?: Array<Record<string, unknown>>;
}

export interface ExtractResponse {
  frontmatter: DatasheetFrontmatter;
  body: string;
  dsheet_content: string;
  dsheet_filename: string;
  method: 'text' | 'vision';
  source_filename: string;
  source_file_id?: string;
  validation: { valid: boolean; errors: Array<{ field: string; message: string }> };
  footprint_result?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Phase A: Detect footprint bbox (cheap — no dimension extraction)
// ---------------------------------------------------------------------------

export async function detectFootprint(fileId: string): Promise<DetectFootprintResponse> {
  return post<DetectFootprintResponse>(`${API_BASE}/footprint/detect`, { file_id: fileId });
}

export async function detectFootprintUpload(
  fileBase64: string, mimeType: string, filename: string,
): Promise<DetectFootprintResponse> {
  return post<DetectFootprintResponse>(`${API_BASE}/footprint/detect`, {
    file_base64: fileBase64, mime_type: mimeType, filename,
  });
}

// ---------------------------------------------------------------------------
// Phase B: Extract from user-approved bbox (expensive — Claude Vision)
// ---------------------------------------------------------------------------

export async function extractCroppedFootprint(
  pagePngBase64: string,
  bbox: { x1: number; y1: number; x2: number; y2: number },
  outputFolderId?: string,
  sourceFilename?: string,
  userHint?: string,
): Promise<ExtractCroppedResponse> {
  return post<ExtractCroppedResponse>(`${API_BASE}/footprint/extract-cropped`, {
    page_png_base64: pagePngBase64,
    bbox,
    output_folder_id: outputFolderId,
    source_filename: sourceFilename,
    user_hint: userHint,
  });
}

// ---------------------------------------------------------------------------
// Full datasheet extraction (text + frontmatter)
// ---------------------------------------------------------------------------

export async function extractFromDrive(fileId: string, category?: string): Promise<ExtractResponse> {
  return post<ExtractResponse>(`${API_BASE}/extract`, { file_id: fileId, category });
}

export async function extractFromUpload(
  fileBase64: string, mimeType: string, filename: string,
  outputFolderId?: string, category?: string,
): Promise<ExtractResponse> {
  return post<ExtractResponse>(`${API_BASE}/extract-upload`, {
    file_base64: fileBase64, mime_type: mimeType, filename,
    output_folder_id: outputFolderId, category,
  });
}

// ---------------------------------------------------------------------------
// SSE streaming extraction with progress events
// ---------------------------------------------------------------------------

export interface ExtractProgressEvent {
  step: string;
  progress: number;
  message: string;
}

/**
 * Extract from Drive with real-time SSE progress events.
 * Calls /extract-stream which yields SSE events during each pipeline phase.
 * The onProgress callback fires for each intermediate step.
 * Returns the final ExtractResponse on completion.
 */
export async function extractFromDriveStream(
  fileId: string,
  onProgress: (event: ExtractProgressEvent) => void,
): Promise<ExtractResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/extract-stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ file_id: fileId }),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Extract stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  return new Promise<ExtractResponse>((resolve, reject) => {
    let resolved = false;

    /** Parse SSE events from text, returns true if a terminal event was found */
    function parseSSEEvents(text: string): boolean {
      const parts = text.split('\n\n');
      for (const part of parts) {
        if (!part.trim()) continue;
        const lines = part.split('\n');
        let eventType = '';
        let eventData = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData = line.slice(6);
        }
        if (!eventType || !eventData) continue;
        try {
          const parsed = JSON.parse(eventData);
          if (eventType === 'progress') {
            onProgress(parsed as ExtractProgressEvent);
          } else if (eventType === 'complete') {
            resolved = true;
            resolve(parsed.result as ExtractResponse);
            return true;
          } else if (eventType === 'error') {
            resolved = true;
            reject(new Error(parsed.error || 'Extraction failed'));
            return true;
          }
        } catch (parseErr) {
          console.warn('[extractStream] Malformed SSE event:', eventType, eventData, parseErr);
        }
      }
      return false;
    }

    function processChunk() {
      reader.read().then(({ done, value }) => {
        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }

        if (done) {
          // Flush remaining buffer — the complete event may be here
          if (buffer.trim()) {
            parseSSEEvents(buffer);
          }
          if (!resolved) {
            reject(new Error('Stream ended without completion'));
          }
          return;
        }

        // Parse complete events from buffer, keep any trailing partial chunk
        const lastDoubleNewline = buffer.lastIndexOf('\n\n');
        if (lastDoubleNewline >= 0) {
          const complete = buffer.substring(0, lastDoubleNewline + 2);
          buffer = buffer.substring(lastDoubleNewline + 2);
          if (parseSSEEvents(complete)) {
            return; // terminal event found
          }
        }

        // Continue reading
        processChunk();
      }).catch(reject);
    }

    processChunk();
  });
}

// ---------------------------------------------------------------------------
// Save extracted.json to Drive alongside the original file
// ---------------------------------------------------------------------------

export async function saveExtractedToDrive(
  sourceFileId: string | undefined,
  sourceFilename: string,
  extracted: Record<string, unknown>,
  footprintJson?: Record<string, unknown> | null,
  croppedPngBase64?: string | null,
): Promise<{ folder_id: string; folder_name: string; saved_files: string[] }> {
  return post(`${API_BASE}/save-extracted`, {
    source_file_id: sourceFileId,
    source_filename: sourceFilename,
    extracted,
    footprint_json: footprintJson,
    cropped_png_base64: croppedPngBase64,
  });
}

// ---------------------------------------------------------------------------
// Footprint correction via Claude chat (SSE)
// ---------------------------------------------------------------------------

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface FootprintCorrectionResult {
  footprint: Record<string, unknown>;
  dimensions: Record<string, unknown>;
  message: string;
}

/**
 * Send a correction prompt to Claude with the current footprint JSON.
 * Returns the corrected footprint via SSE streaming.
 */
export async function correctFootprintStream(
  prompt: string,
  footprint: Record<string, unknown>,
  history: ChatMessage[],
  onProgress: (event: ExtractProgressEvent) => void,
): Promise<FootprintCorrectionResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('auth_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/footprint/correct-stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ prompt, footprint, history }),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Correction stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  return new Promise<FootprintCorrectionResult>((resolve, reject) => {
    let resolved = false;

    function parseSSEEvents(text: string): boolean {
      const parts = text.split('\n\n');
      for (const part of parts) {
        if (!part.trim()) continue;
        const lines = part.split('\n');
        let eventType = '', eventData = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData = line.slice(6);
        }
        if (!eventType || !eventData) continue;
        try {
          const parsed = JSON.parse(eventData);
          if (eventType === 'progress') onProgress(parsed);
          else if (eventType === 'complete') { resolved = true; resolve(parsed); return true; }
          else if (eventType === 'error') { resolved = true; reject(new Error(parsed.error)); return true; }
        } catch (e) {
          console.warn('[correctStream] Malformed SSE event:', eventType, eventData, e);
        }
      }
      return false;
    }

    function processChunk() {
      reader.read().then(({ done, value }) => {
        if (value) buffer += decoder.decode(value, { stream: true });
        if (done) {
          if (buffer.trim()) parseSSEEvents(buffer);
          if (!resolved) reject(new Error('Stream ended without completion'));
          return;
        }
        const lastNL = buffer.lastIndexOf('\n\n');
        if (lastNL >= 0) {
          if (parseSSEEvents(buffer.substring(0, lastNL + 2))) return;
          buffer = buffer.substring(lastNL + 2);
        }
        processChunk();
      }).catch(reject);
    }

    processChunk();
  });
}
